<template>
  <div class="w-full overflow-x-auto border border-[#d8d8e2] rounded-lg">
    <table class="w-full min-w-[900px] border-collapse">
      <thead>
        <tr class="bg-[#f0f0f4]">
          <th
            v-for="col in columns"
            :key="col.key"
            class="px-4 py-2.5 text-sm font-bold text-[#272735] text-center"
          >
            {{ col.label }}
          </th>
        </tr>
      </thead>

      <tbody>
        <tr
          v-for="item in doctors"
          :key="item.doctor.id + '-' + (item.id ?? 'no-bio')"
          class="border-t border-[#e8e8ed] bg-white hover:bg-[#fafafa] transition-colors"
          :data-testid="`biometry-row-${item.doctor.id}`"
        >
          <td class="px-4 py-3">
            <div class="flex items-center gap-3">
              <NpsScoreAvatar
                :nps-score="item.doctor.npsScore"
                :avatar-url="item.doctor.photoUrl"
                :stroke-width="4"
                role="img"
                :aria-label="item.doctor.name"
              >
                <InitialsFallback :name="item.doctor.name" />
              </NpsScoreAvatar>
              <div class="flex flex-col gap-0.5 overflow-hidden">
                <div class="flex items-center gap-2">
                  <span class="text-[13px] font-semibold text-[#16161d] truncate">
                    {{ formatDoctorShortName(item.doctor.name) }}
                  </span>
                </div>
                <span class="text-[11px] text-[#52526f] text-left">
                  {{ formatCrm(item.doctor.crm, item.doctor.crmUf) }}
                </span>
              </div>
            </div>
          </td>

          <td class="px-4 py-3">
            <ScheduleSummary
              v-if="item.schedules.length"
              :schedules="item.schedules"
              align="center"
              dense
            />
            <span v-else class="text-[13px] text-[#16161d]">-</span>
          </td>

          <td class="px-4 py-3">
            <template v-if="item.createdAt">
              <div class="flex flex-col items-center gap-0.5">
                <span class="text-[13px] text-[#16161d]">
                  {{ formatDateInOffset(item.createdAt) }}
                </span>
                <span class="text-[12px] font-bold text-[#16161d]">
                  às {{ formatHourMinuteWithZoneInOffset(item.createdAt) }}
                </span>
              </div>
            </template>
            <span v-else class="text-[13px] text-[#16161d]">-</span>
          </td>

          <td class="px-4 py-3 text-center">
            <div class="flex justify-center">
              <StatusBadge :status="item.status" />
            </div>
          </td>

          <td class="px-4 py-3 text-center flex items-center justify-center">
            <OutlineButton
              v-if="item.status === FacialBiometryStatus.NOT_SUBMITTED"
              :data-testid="`notify-button-${item.doctor.id}`"
              text="Notificar"
              width="auto"
              height="revert-layer"
              @click="emit('notify', item)"
            />
            <OutlineButton
              v-else-if="item.status === FacialBiometryStatus.PENDING"
              :data-testid="`evaluate-button-${item.doctor.id}`"
              text="Avaliar"
              width="auto"
              height="revert-layer"
              @click="emit('evaluate', item)"
            />
            <GhostButton
              v-else
              :data-testid="`history-button-${item.doctor.id}`"
              text="Ver Histórico"
              width="auto"
              height="revert-layer"
              @click="emit('viewHistory', item)"
            />
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import StatusBadge from '@/components/designSystem/Badges/StatusBadge.vue'
import OutlineButton from '@/components/designSystem/Buttons/OutlineButton/index.vue'
import GhostButton from '@/components/designSystem/Buttons/GhostButton/index.vue'
import { FacialBiometryStatus } from '@/constants/facial-biometry.constants'
import type { FacialBiometryDoctorItem } from '@/types/facial-biometry.types'
import { formatCrm } from '@/utils/formatters'
import { formatDoctorShortName } from '@/utils/string'
import { formatDateInOffset, formatHourMinuteWithZoneInOffset } from '@/utils/date'
import NpsScoreAvatar from '@/components/designSystem/AvatarUser/NpsScoreAvatar.vue'
import InitialsFallback from './InitialsFallback.vue'
import ScheduleSummary from './ScheduleSummary.vue'

defineProps<{
  doctors: FacialBiometryDoctorItem[]
}>()

const emit = defineEmits<{
  evaluate: [item: FacialBiometryDoctorItem]
  viewHistory: [item: FacialBiometryDoctorItem]
  notify: [item: FacialBiometryDoctorItem]
}>()

const columns = [
  { key: 'doctor', label: 'Médico' },
  { key: 'schedule', label: 'Escala' },
  { key: 'date', label: 'Data de envio' },
  { key: 'status', label: 'Status' },
  { key: 'actions', label: 'Ações' }
]
</script>
